import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/api";
import { ArrowRight, CheckCircle2, BarChart3 } from "lucide-react";

export default function Bargaining() {
  const { t, lang } = useLang();
  const [status, setStatus] = useState({ current_stage: 1, update_en: "", update_fr: "" });
  useEffect(() => { api.get("/bargaining-status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  const stages = [1, 2, 3, 4, 5];
  const proposals = t("bargaining.proposalItems");

  return (
    <div data-testid="bargaining-page">
      <section className="bg-gradient-to-br from-pink-50 via-white to-rose-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900">{t("bargaining.title")}</h1>
          <p className="text-lg text-slate-600 mt-3 max-w-2xl">{t("bargaining.sub")}</p>
          <div className="h-1 w-20 cupe-gradient rounded-full mt-4" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 live-dot" />
            <span className="text-xs uppercase tracking-widest font-semibold text-rose-600">Live</span>
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-8">{t("bargaining.progressTitle")}</h2>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            {stages.map((n, i) => (
              <React.Fragment key={n}>
                <div className="flex md:flex-col items-center md:items-start gap-3 md:gap-2 flex-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg ${n <= status.current_stage ? "cupe-gradient" : "bg-slate-200 text-slate-500"}`}>{n}</div>
                  <div className={`text-sm font-semibold ${n <= status.current_stage ? "text-slate-900" : "text-slate-400"}`}>{t(`bargainingStages.${n}`)}</div>
                </div>
                {i < stages.length - 1 && <div className={`hidden md:block h-1 flex-1 rounded ${n < status.current_stage ? "cupe-gradient" : "bg-slate-200"}`} />}
              </React.Fragment>
            ))}
          </div>

          <div className="bg-pink-50/40 border border-pink-100 rounded-xl p-5">
            <div className="text-xs uppercase tracking-widest text-[#d81b7a] font-semibold mb-2">{t("bargaining.latestUpdate")}</div>
            <p className="text-slate-700 leading-relaxed text-lg">{lang === "fr" ? status.update_fr : status.update_en}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
            <h3 className="font-display text-xl font-bold text-slate-900 mb-4">{t("bargaining.proposalsTitle")}</h3>
            <ul className="space-y-3">
              {Array.isArray(proposals) && proposals.map((p, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <CheckCircle2 className="text-[#d81b7a] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-slate-700">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="cupe-gradient rounded-2xl p-6 md:p-8 text-white">
            <BarChart3 size={32} className="mb-3" />
            <h3 className="font-display text-xl font-bold mb-2">{t("bargaining.surveysTitle")}</h3>
            <p className="text-white/90 mb-6">{t("bargaining.surveysSub")}</p>
            <Link to="/portal" className="inline-flex items-center gap-2 bg-white text-[#d81b7a] px-5 py-3 rounded-md font-semibold hover:bg-pink-50 transition" data-testid="bargaining-portal-btn">
              {t("bargaining.goPortal")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
